/**
 * Gallery Random Spacing Module
 * Adds staggered/random gaps between gallery items for visual interest
 */

class GalleryRandomSpacing {
  constructor(container, options = {}) {
    this.container = container;
    this.options = {
      // Random spacing ranges (in rem units) - additional to base margins
      mobile: {
        min: 0,
        max: 3
      },
      desktop: {
        min: 0,
        max: 8
      },
      // Seed for consistent randomness across page loads
      seed: options.seed || this.generateSeed(),
      // Whether to apply to margins or padding
      property: options.property || 'margin',
      // Directions to apply spacing (only vertical)
      directions: options.directions || ['top', 'bottom']
    };
    
    this.init();
  }

  generateSeed() {
    // Generate seed based on container ID or content for consistency
    const id = this.container.id || this.container.className || '';
    return id.split('').reduce((a, b) => {
      a = ((a << 5) - a) + b.charCodeAt(0);
      return a & a;
    }, 0);
  }

  // Seeded random number generator for consistent results
  seededRandom(seed) {
    const x = Math.sin(seed) * 10000;
    return x - Math.floor(x);
  }

  getColumnTopOffset(columnIndex, isDesktop = false) {
    // No column offset - CSS handles consistent spacing
    return 0;
  }

  getRandomSpacing(index, direction, isDesktop = false, columnIndex = 0, totalColumns = 1) {
    // Disabled dramatic random spacing - CSS handles consistent spacing instead
    return 0;
  }

  applyRandomSpacing() {
    // Disable random spacing on mobile to prevent layout conflicts
    const isDesktop = window.innerWidth >= 990;
    if (!isDesktop) {
      console.log('Skipping random spacing on mobile to prevent 2-column layout conflicts');
      return;
    }
    
    const items = this.container.querySelectorAll('li:not(.grid-sizer):not(.gutter-sizer), .grid-item, .tile, .gallery-item');

    // Create a map of items by their actual horizontal position (column)
    const itemsByColumn = new Map();
    const containerRect = this.container.getBoundingClientRect();
    
    items.forEach((item, index) => {
      const itemRect = item.getBoundingClientRect();
      const relativeLeft = itemRect.left - containerRect.left;
      
      // Find existing column or create new one (tolerance of 20px for alignment)
      let columnKey = null;
      for (const [key, _] of itemsByColumn) {
        if (Math.abs(key - relativeLeft) < 20) {
          columnKey = key;
          break;
        }
      }
      
      if (columnKey === null) {
        columnKey = relativeLeft;
        itemsByColumn.set(columnKey, []);
      }
      
      itemsByColumn.get(columnKey).push({ item, index, left: relativeLeft });
    });

    // Sort columns by left position
    const sortedColumns = Array.from(itemsByColumn.keys()).sort((a, b) => a - b);
    const totalColumns = sortedColumns.length;

    // Track first item in each column
    const firstItemInColumn = new Set();
    for (const [columnKey, columnItems] of itemsByColumn) {
      if (columnItems.length > 0) {
        // Sort by vertical position to find the topmost item
        columnItems.sort((a, b) => {
          const aRect = a.item.getBoundingClientRect();
          const bRect = b.item.getBoundingClientRect();
          return aRect.top - bRect.top;
        });
        firstItemInColumn.add(columnItems[0].item);
      }
    }

    items.forEach((item, index) => {
      // Skip if item already has random spacing applied
      if (item.dataset.randomSpacingApplied) return;

      // Find which visual column this item is actually in
      const itemRect = item.getBoundingClientRect();
      const relativeLeft = itemRect.left - containerRect.left;
      
      let columnIndex = 0;
      for (let i = 0; i < sortedColumns.length; i++) {
        if (Math.abs(sortedColumns[i] - relativeLeft) < 20) {
          columnIndex = i;
          break;
        }
      }

      const spacingVars = {};
      const isFirstInColumn = firstItemInColumn.has(item);
      
      this.options.directions.forEach(direction => {
        // Handle first items in each column differently
        if (direction === 'top' && isFirstInColumn) {
          if (columnIndex === 0) {
            // First column: always flush with top
            spacingVars[`--random-spacing-${direction}`] = '0rem';
          } else {
            // Other columns: slight random top margin for column offset
            const columnTopOffset = this.getColumnTopOffset(columnIndex, isDesktop);
            spacingVars[`--random-spacing-${direction}`] = `${columnTopOffset}rem`;
          }
        } else {
          const spacing = this.getRandomSpacing(index, direction, isDesktop, columnIndex, totalColumns);
          const varName = `--random-spacing-${direction}`;
          spacingVars[varName] = `${spacing}rem`;
        }
      });

      // Apply CSS custom properties
      Object.entries(spacingVars).forEach(([prop, value]) => {
        item.style.setProperty(prop, value);
      });

      // Apply only vertical spacing using CSS - ADD to existing margin
      const currentMarginTop = getComputedStyle(item).marginTop;
      const currentMarginBottom = getComputedStyle(item).marginBottom;
      item.style.setProperty('margin-top', `calc(${currentMarginTop} + var(--random-spacing-top, 0rem))`);
      item.style.setProperty('margin-bottom', `calc(${currentMarginBottom} + var(--random-spacing-bottom, 0rem))`);

      // Mark as processed
      item.dataset.randomSpacingApplied = 'true';
    });
  }

  init() {
    // Apply initial spacing
    this.applyRandomSpacing();

    // Reapply on window resize for responsive behavior
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        // Clear existing spacing
        const items = this.container.querySelectorAll('[data-random-spacing-applied]');
        items.forEach(item => {
          delete item.dataset.randomSpacingApplied;
        });
        // Reapply with new responsive values
        this.applyRandomSpacing();
      }, 250);
    });
  }

  // Method to refresh spacing (useful for dynamic content)
  refresh() {
    const items = this.container.querySelectorAll('[data-random-spacing-applied]');
    items.forEach(item => {
      delete item.dataset.randomSpacingApplied;
    });
    this.applyRandomSpacing();
  }
}

// Auto-initialize for galleries with data attribute or common gallery selectors
document.addEventListener('DOMContentLoaded', () => {
  // Primary initialization for galleries with explicit data attribute
  const galleries = document.querySelectorAll('[data-random-spacing="true"]');
  galleries.forEach(gallery => {
    new GalleryRandomSpacing(gallery);
  });
  
  // Additional initialization for specific gallery patterns only
  const additionalGalleries = document.querySelectorAll(`
    .image-gallery ul,
    .masonry-gallery ul,
    .product-media-gallery ul,
    section[id*="Gallery"] ul,
    section[id*="Grid"] ul,
    .gallery-section ul,
    .grid-section ul
  `);
  
  additionalGalleries.forEach(gallery => {
    // Avoid double initialization and menu/navigation elements
    const isNavigation = gallery.closest('nav, header, .menu, .navigation, [role="navigation"]');
    if (!gallery.hasAttribute('data-random-spacing') && 
        !gallery.querySelector('[data-random-spacing-applied]') && 
        !isNavigation) {
      new GalleryRandomSpacing(gallery);
    }
  });
});

// Export for manual initialization
window.GalleryRandomSpacing = GalleryRandomSpacing;